import {
  isSectionEnabled,
  landingPageContent,
} from '../../content/landingPageContent';

const heroImageSources = {
  avif:
    '/hero/hero-640.avif 640w, /hero/hero-960.avif 960w, /hero/hero-1280.avif 1280w, /hero/hero-1600.avif 1600w, /hero/hero-1920.avif 1920w',
  webp:
    '/hero/hero-640.webp 640w, /hero/hero-960.webp 960w, /hero/hero-1280.webp 1280w, /hero/hero-1600.webp 1600w, /hero/hero-1920.webp 1920w',
  jpeg:
    '/hero/hero-640.jpg 640w, /hero/hero-960.jpg 960w, /hero/hero-1280.jpg 1280w, /hero/hero-1600.jpg 1600w, /hero/hero-1920.jpg 1920w',
};

export function Hero() {
  const { hero } = landingPageContent;

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition =
        element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section className="relative flex min-h-[100svh] items-center overflow-hidden bg-[#100f14] pt-20 dark:bg-[#100f14] sm:min-h-screen">
      {/* SVG Definitions for clipping */}
      <svg className="absolute w-0 h-0">
        <defs>
          <clipPath
            id="heroClip"
            clipPathUnits="objectBoundingBox"
            transform="scale(0.000694444 0.01)"
          >
            <path d="M0 0 L1440 0 L1440 94 Q1080 90 720 94 T0 94 Z" />
          </clipPath>
        </defs>
      </svg>

      {/* Background Image with Overlay - Clipped at curve */}
      <div
        className="absolute inset-0 z-0"
        style={{ clipPath: 'url(#heroClip)' }}
      >
        <picture>
          <source
            type="image/avif"
            srcSet={heroImageSources.avif}
            sizes="100vw"
          />
          <source
            type="image/webp"
            srcSet={heroImageSources.webp}
            sizes="100vw"
          />
          <source
            type="image/jpeg"
            srcSet={heroImageSources.jpeg}
            sizes="100vw"
          />
          <img
            src={hero.imageSrc}
            alt={hero.imageAlt}
            width={1920}
            height={1072}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="h-full w-full object-cover object-[62%_center] sm:object-[68%_center] lg:object-center"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/72 to-black/35 sm:from-black/80 sm:via-black/60 sm:to-black/40" />
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 py-20 sm:py-24 lg:px-8">
        <div className="max-w-[19rem] sm:max-w-2xl">
          <h1 className="text-white mb-6">{hero.title}</h1>

          <p className="text-white/90 text-xl mb-8 leading-relaxed">
            {hero.subtitle}
          </p>

          <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap">
            {isSectionEnabled(hero.primaryCta.sectionKey) && (
              <button
                onClick={() =>
                  scrollToSection(hero.primaryCta.targetId)
                }
                className="w-full rounded-md bg-[#8A651E] px-8 py-3 text-white transition-all duration-300 hover:bg-[#6F5319] hover:shadow-lg hover:shadow-[#8A651E]/30 sm:w-auto"
              >
                {hero.primaryCta.label}
              </button>
            )}
            {isSectionEnabled(hero.secondaryCta.sectionKey) && (
              <button
                onClick={() =>
                  scrollToSection(hero.secondaryCta.targetId)
                }
                className="w-full rounded-md border-2 border-white px-8 py-3 text-white transition-all duration-300 hover:bg-white hover:text-[#111111] sm:w-auto"
              >
                {hero.secondaryCta.label}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Curved Gold Ribbon Divider */}
      <div className="absolute inset-0 z-20 pointer-events-none">
        <svg
          className="w-full h-full"
          viewBox="0 0 1440 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient
              id="goldGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="#d6b25a" />
              <stop offset="50%" stopColor="#B08A2E" />
              <stop offset="100%" stopColor="#b1842f" />
            </linearGradient>
          </defs>
          <path
            d="M0 94 Q360 98 720 94 T1440 94"
            stroke="url(#goldGradient)"
            strokeWidth="3"
            fill="none"
          />
        </svg>
      </div>
    </section>
  );
}
